/**
 * ReceiptPrint - Componente centralizado para impressão de cupom não fiscal
 * Usado em PDV.js, Sales.js e CashClosing.js
 */

const ReceiptPrint = ({ sale, companyName, companyLogo, isReprint = false }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('pt-BR'),
      time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const paymentLabels = {
    dinheiro: 'DINHEIRO',
    pix: 'PIX',
    pix_cnpj: 'PIX - CNPJ',
    cartao_credito: 'CARTÃO CRÉDITO',
    cartao_debito: 'CARTÃO DÉBITO',
    crediario_bemol: 'CREDIÁRIO BEMOL',
    vale_mercadoria: 'VALE MERCADORIA',
    avancard: 'AVANCARD',
    multiplo: 'MÚLTIPLO',
    pendente: 'PENDENTE'
  };

  if (!sale) return null;

  const { date, time } = formatDateTime(sale.created_at);

  return (
    <div id={isReprint ? "receipt-print-reprint" : "receipt-print"} className="receipt-container">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #receipt-print, #receipt-print *, #receipt-print-reprint, #receipt-print-reprint * {
            visibility: visible;
          }
          #receipt-print, #receipt-print-reprint {
            position: absolute;
            left: 0;
            top: 0;
            width: 80mm;
            padding: 5mm;
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
          }
        }
        .receipt-container {
          width: 280px;
          padding: 16px;
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.4;
          background: white;
        }
        .receipt-logo {
          text-align: center;
          margin-bottom: 8px;
        }
        .receipt-logo img {
          max-width: 120px;
          max-height: 60px;
          object-fit: contain;
        }
        .receipt-header {
          text-align: center;
          border-bottom: 1px dashed #000;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .receipt-company {
          font-weight: bold;
          font-size: 14px;
        }
        .receipt-title {
          font-size: 11px;
          margin-top: 4px;
        }
        .receipt-info {
          border-bottom: 1px dashed #000;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .receipt-info-row {
          display: flex;
          justify-content: space-between;
          font-size: 11px;
          margin-bottom: 2px;
        }
        .receipt-items {
          border-bottom: 1px dashed #000;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .receipt-item {
          margin-bottom: 4px;
        }
        .receipt-item-name {
          font-size: 11px;
          font-weight: bold;
        }
        .receipt-item-details {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          color: #666;
        }
        .receipt-totals {
          border-bottom: 1px dashed #000;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .receipt-total-row {
          display: flex;
          justify-content: space-between;
          font-size: 11px;
          margin-bottom: 2px;
        }
        .receipt-total-row.total {
          font-weight: bold;
          font-size: 14px;
          margin-top: 4px;
        }
        .receipt-payment {
          border-bottom: 1px dashed #000;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .receipt-footer {
          text-align: center;
          font-size: 10px;
          color: #666;
        }
        .receipt-reprint {
          text-align: center;
          font-size: 11px;
          font-weight: bold;
          margin-top: 8px;
          padding: 4px;
          border: 1px dashed #000;
        }
        .receipt-cancelled {
          text-align: center;
          font-size: 12px;
          font-weight: bold;
          color: #c00;
          margin-top: 8px;
          padding: 4px;
          border: 2px solid #c00;
        }
      `}</style>

      {/* Logo da empresa */}
      {companyLogo && (
        <div className="receipt-logo">
          <img src={companyLogo} alt="Logo" />
        </div>
      )}

      <div className="receipt-header">
        <div className="receipt-company">{companyName || 'EMPRESA'}</div>
        <div className="receipt-title">CUPOM NÃO FISCAL</div>
      </div>

      <div className="receipt-info">
        <div className="receipt-info-row">
          <span>Data:</span>
          <span>{date} {time}</span>
        </div>
        <div className="receipt-info-row">
          <span>Venda:</span>
          <span>{sale.sale_number}</span>
        </div>
        <div className="receipt-info-row">
          <span>Cliente:</span>
          <span>{sale.client_name}</span>
        </div>
        <div className="receipt-info-row">
          <span>Vendedor:</span>
          <span>{sale.seller_name}</span>
        </div>
      </div>

      <div className="receipt-items">
        <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '11px' }}>ITENS</div>
        {sale.items?.map((item, idx) => (
          <div key={idx} className="receipt-item">
            <div className="receipt-item-name">{item.product_name}</div>
            <div className="receipt-item-details">
              <span>{item.quantity}x {formatCurrency(item.unit_price)}</span>
              <span>{formatCurrency(item.total)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="receipt-totals">
        <div className="receipt-total-row">
          <span>Subtotal:</span>
          <span>{formatCurrency(sale.subtotal)}</span>
        </div>
        {sale.discount > 0 && (
          <div className="receipt-total-row">
            <span>Desconto:</span>
            <span>-{formatCurrency(sale.discount)}</span>
          </div>
        )}
        <div className="receipt-total-row total">
          <span>TOTAL:</span>
          <span>{formatCurrency(sale.total)}</span>
        </div>
      </div>

      <div className="receipt-payment">
        <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '11px' }}>PAGAMENTO</div>
        {sale.payment_methods && sale.payment_methods.length > 0 ? (
          sale.payment_methods.map((pm, idx) => (
            <div key={idx} className="receipt-total-row">
              <span>{paymentLabels[pm.method] || pm.method}:</span>
              <span>{formatCurrency(pm.amount)}</span>
            </div>
          ))
        ) : (
          <div className="receipt-total-row">
            <span>{paymentLabels[sale.payment_method] || sale.payment_method}:</span>
            <span>{formatCurrency(sale.amount_paid || sale.total)}</span>
          </div>
        )}
        {sale.payment_method === 'dinheiro' && sale.change > 0 && (
          <div className="receipt-total-row" style={{ marginTop: '4px' }}>
            <span>Troco:</span>
            <span>{formatCurrency(sale.change)}</span>
          </div>
        )}
      </div>

      <div className="receipt-footer">
        <p>Obrigado pela preferência!</p>
        <p>Volte sempre!</p>
      </div>

      {/* Indicador de 2ª via para reimpressões */}
      {isReprint && (
        <div className="receipt-reprint">
          *** 2ª VIA ***
        </div>
      )}

      {/* Indicador de cancelamento */}
      {sale.payment_status === 'cancelado' && (
        <div className="receipt-cancelled">
          *** CANCELADO ***
        </div>
      )}
    </div>
  );
};

/**
 * DANFEPrint - Componente para impressão de DANFE NFC-e simplificado
 */
const DANFEPrint = ({ nfce, sale, companyData }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  if (!nfce || !sale) return null;

  return (
    <div id="danfe-print" className="danfe-container">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #danfe-print, #danfe-print * { visibility: visible; }
          #danfe-print {
            position: absolute;
            left: 0;
            top: 0;
            width: 80mm;
            padding: 3mm;
            font-family: Arial, sans-serif;
            font-size: 10px;
          }
        }
        .danfe-container {
          width: 280px;
          padding: 12px;
          font-family: Arial, sans-serif;
          font-size: 10px;
          background: white;
          border: 1px solid #ccc;
        }
        .danfe-header {
          text-align: center;
          border-bottom: 1px solid #000;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .danfe-title {
          font-weight: bold;
          font-size: 11px;
        }
        .danfe-empresa {
          font-size: 9px;
          margin-top: 4px;
        }
        .danfe-section {
          border-bottom: 1px solid #ccc;
          padding: 6px 0;
          margin-bottom: 6px;
        }
        .danfe-row {
          display: flex;
          justify-content: space-between;
          font-size: 9px;
          margin: 2px 0;
        }
        .danfe-qrcode {
          text-align: center;
          padding: 10px;
          background: #f5f5f5;
          margin: 8px 0;
        }
      `}</style>

      <div className="danfe-header">
        <div className="danfe-title">DANFE NFC-e</div>
        <div className="danfe-title">Documento Auxiliar da NFC-e</div>
        <div className="danfe-empresa">
          <strong>{companyData?.name || 'EMPRESA'}</strong><br/>
          CNPJ: {companyData?.cnpj || '00.000.000/0001-00'}<br/>
          {companyData?.address || 'Endereço'}
        </div>
      </div>

      <div className="danfe-section">
        <div className="danfe-row">
          <span>NFC-e nº:</span>
          <span>{nfce?.numero || '000000'}</span>
        </div>
        <div className="danfe-row">
          <span>Série:</span>
          <span>{nfce?.serie || '1'}</span>
        </div>
        <div className="danfe-row">
          <span>Data/Hora:</span>
          <span>{new Date().toLocaleString('pt-BR')}</span>
        </div>
      </div>

      <div className="danfe-section">
        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>ITENS</div>
        {sale?.items?.map((item, idx) => (
          <div key={idx} style={{ marginBottom: '4px', fontSize: '9px' }}>
            <div>{item.product_name}</div>
            <div className="danfe-row">
              <span>{item.quantity} x {formatCurrency(item.unit_price)}</span>
              <span>{formatCurrency(item.total)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="danfe-section">
        <div className="danfe-row" style={{ fontWeight: 'bold' }}>
          <span>TOTAL:</span>
          <span>{formatCurrency(sale?.total)}</span>
        </div>
      </div>

      <div className="danfe-qrcode">
        <p style={{ fontSize: '8px', color: '#666' }}>
          QR Code para consulta via app
        </p>
        <div style={{ width: '100px', height: '100px', background: '#ddd', margin: '8px auto' }}>
          [QR CODE]
        </div>
      </div>

      <div style={{ textAlign: 'center', fontSize: '8px', color: '#666' }}>
        <p>Consulte pela chave de acesso em:</p>
        <p>www.nfce.fazenda.gov.br</p>
        <p style={{ wordBreak: 'break-all', marginTop: '4px' }}>
          {nfce?.chave || '00000000000000000000000000000000000000000000'}
        </p>
      </div>
    </div>
  );
};

/**
 * Função utilitária para imprimir cupom em nova janela
 * @param {Object} sale - Dados da venda
 * @param {Object} companyData - Dados da empresa (name, logo)
 * @param {boolean} isReprint - Se é reimpressão (2ª via)
 */
const printReceipt = (sale, companyData, isReprint = false) => {
  const printWindow = window.open('', '_blank', 'width=400,height=600');
  if (!printWindow) {
    console.error('Pop-up bloqueado');
    return false;
  }

  const paymentLabels = {
    dinheiro: 'DINHEIRO',
    pix: 'PIX',
    pix_cnpj: 'PIX - CNPJ',
    cartao_credito: 'CARTÃO CRÉDITO',
    cartao_debito: 'CARTÃO DÉBITO',
    crediario_bemol: 'CREDIÁRIO BEMOL',
    vale_mercadoria: 'VALE MERCADORIA',
    avancard: 'AVANCARD',
    multiplo: 'MÚLTIPLO',
    pendente: 'PENDENTE'
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('pt-BR'),
      time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const { date, time } = formatDateTime(sale.created_at);

  const logoHtml = companyData?.logo 
    ? `<div style="text-align: center; margin-bottom: 8px;"><img src="${companyData.logo}" alt="Logo" style="max-width: 120px; max-height: 60px; object-fit: contain;" /></div>`
    : '';

  const itemsHtml = sale.items?.map(item => `
    <div style="margin-bottom: 4px;">
      <div style="font-weight: bold;">${item.product_name}</div>
      <div style="display: flex; justify-content: space-between; font-size: 11px;">
        <span>${item.quantity}x ${formatCurrency(item.unit_price)}</span>
        <span>${formatCurrency(item.total)}</span>
      </div>
    </div>
  `).join('') || '';

  let paymentsHtml = '';
  if (sale.payment_methods && sale.payment_methods.length > 0) {
    paymentsHtml = sale.payment_methods.map(pm => `
      <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
        <span>${paymentLabels[pm.method] || pm.method}:</span>
        <span>${formatCurrency(pm.amount)}</span>
      </div>
    `).join('');
  } else {
    paymentsHtml = `
      <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
        <span>${paymentLabels[sale.payment_method] || sale.payment_method}:</span>
        <span>${formatCurrency(sale.amount_paid || sale.total)}</span>
      </div>
    `;
  }

  if (sale.payment_method === 'dinheiro' && sale.change > 0) {
    paymentsHtml += `
      <div style="display: flex; justify-content: space-between; margin-top: 4px;">
        <span>Troco:</span>
        <span>${formatCurrency(sale.change)}</span>
      </div>
    `;
  }

  const reprintHtml = isReprint 
    ? '<div style="text-align: center; font-size: 11px; font-weight: bold; margin-top: 8px; padding: 4px; border: 1px dashed #000;">*** 2ª VIA ***</div>' 
    : '';

  const cancelledHtml = sale.payment_status === 'cancelado'
    ? '<div style="text-align: center; font-size: 12px; font-weight: bold; color: #c00; margin-top: 8px; padding: 4px; border: 2px solid #c00;">*** CANCELADO ***</div>'
    : '';

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Cupom - ${sale.sale_number}</title>
      <style>
        body {
          margin: 0;
          padding: 10px;
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.4;
        }
        .receipt {
          width: 280px;
          padding: 16px;
          background: white;
        }
        .header {
          text-align: center;
          border-bottom: 1px dashed #000;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .company {
          font-weight: bold;
          font-size: 14px;
        }
        .section {
          border-bottom: 1px dashed #000;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 2px;
        }
        .total-row {
          font-weight: bold;
          font-size: 14px;
          margin-top: 4px;
        }
        .footer {
          text-align: center;
          font-size: 10px;
          color: #666;
        }
      </style>
    </head>
    <body>
      <div class="receipt">
        ${logoHtml}
        <div class="header">
          <div class="company">${companyData?.name || 'EMPRESA'}</div>
          <div style="font-size: 11px; margin-top: 4px;">CUPOM NÃO FISCAL</div>
        </div>
        <div class="section">
          <div class="row"><span>Data:</span><span>${date} ${time}</span></div>
          <div class="row"><span>Venda:</span><span>${sale.sale_number}</span></div>
          <div class="row"><span>Cliente:</span><span>${sale.client_name}</span></div>
          <div class="row"><span>Vendedor:</span><span>${sale.seller_name}</span></div>
        </div>
        <div class="section">
          <div style="font-weight: bold; margin-bottom: 4px; font-size: 11px;">ITENS</div>
          ${itemsHtml}
        </div>
        <div class="section">
          <div class="row"><span>Subtotal:</span><span>${formatCurrency(sale.subtotal)}</span></div>
          ${sale.discount > 0 ? `<div class="row"><span>Desconto:</span><span>-${formatCurrency(sale.discount)}</span></div>` : ''}
          <div class="row total-row"><span>TOTAL:</span><span>${formatCurrency(sale.total)}</span></div>
        </div>
        <div class="section">
          <div style="font-weight: bold; margin-bottom: 4px; font-size: 11px;">PAGAMENTO</div>
          ${paymentsHtml}
        </div>
        <div class="footer">
          <p>Obrigado pela preferência!</p>
          <p>Volte sempre!</p>
        </div>
        ${reprintHtml}
        ${cancelledHtml}
      </div>
    </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();

  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 500);

  return true;
};

export { ReceiptPrint, DANFEPrint, printReceipt };
export default ReceiptPrint;
